
import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Crown } from "lucide-react";
import { Profile } from "@/types/auth";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { getMaxStudentsForPlan } from "../utils/planUtils";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";

export const CurrentPlan = ({ profile }: { profile: Profile | null }) => {
  const { language } = useLanguage();
  const { t } = useTranslations("dashboard");
  const dateLocale = language === 'pt' ? ptBR : enUS;

  const planInfo = useMemo(() => {
    if (!profile?.subscription_plan) {
      return {
        title: t("subscription.freePlan"),
        description: t("subscription.basicAccess")
      };
    }

    if (profile.subscription_plan === "trial") {
      const endDateText = profile.subscription_end_date 
        ? format(new Date(profile.subscription_end_date), "dd MMMM yyyy", { locale: dateLocale })
        : t("subscription.thirtyDays");
      
      return {
        title: t("subscription.trialPlan"),
        description: `${t("subscription.fullAccessUntil")} ${endDateText}`
      };
    }

    if (profile.subscription_plan === "pro") {
      return {
        title: t("subscription.professionalPlan"),
        description: t("subscription.planInfo.fullAccess")
      };
    }

    if (profile.subscription_plan === "premium") {
      return {
        title: t("subscription.premiumPlan"),
        description: t("subscription.fullAccessWithSupport")
      };
    }

    return {
      title: t("subscription.freePlan"),
      description: t("subscription.basicAccess")
    };
  }, [profile, t, dateLocale]);

  if (!profile) return null;

  return (
    <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-slate-200 dark:border-slate-700">
      <CardContent className="flex items-center gap-4 p-6">
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Crown className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h3 className="text-xl font-semibold dark:text-white">{planInfo.title}</h3>
          <p className="text-muted-foreground mt-1">
            {planInfo.description} - {t("subscription.planInfo.students")}
          </p>
          
          {profile.subscription_end_date && profile.subscription_status && (
            <div className="mt-2">
              <span className={`text-sm ${
                profile.subscription_status === "active" || profile.subscription_status === "trialing" 
                  ? "text-green-600 dark:text-green-400" 
                  : "text-amber-600 dark:text-amber-400"
              }`}>
                {t("subscription.status")}: {profile.subscription_status === "active" || profile.subscription_status === "trialing" 
                  ? t("subscription.active") 
                  : t("subscription.inactive")}
              </span>
              
              {(profile.subscription_plan !== "free") && 
                <span className="text-sm text-muted-foreground block">
                  {t("subscription.validUntil")} {format(new Date(profile.subscription_end_date), "dd MMMM yyyy", { locale: dateLocale })}
                </span>
              }
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
